import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart3, FileText, MessageSquare, LogOut, Send, Users, Copy, Trash2, ChevronLeft, CheckCircle, Clock } from 'lucide-react';
import { getInvitations, createAdminInvitation, deleteInvitation } from '../api';
import toast from 'react-hot-toast';

const AdminInvitations = () => {
  const navigate = useNavigate();
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) { navigate('/admin'); return; }
    const user = JSON.parse(localStorage.getItem('admin_user') || '{}');
    if (user.role === 'agent') { navigate('/agent'); return; }
    loadInvitations();
  }, [navigate]);

  const loadInvitations = async () => {
    try {
      const res = await getInvitations();
      setInvitations(res.data);
    } catch (err) {
      toast.error('Erreur de chargement');
    }
    setLoading(false);
  };

  const [pendingWaUrl, setPendingWaUrl] = useState(null);

  const handleCreate = async () => {
    if (!phone.trim() && !email.trim()) {
      toast.error('Veuillez entrer un numéro ou un email');
      return;
    }
    setSending(true);
    setPendingWaUrl(null);
    try {
      const res = await createAdminInvitation({ phone: phone.trim() || undefined, email: email.trim() || undefined, message: message.trim() || undefined });

      // Ouvrir WhatsApp automatiquement si un numéro est fourni
      if (res.data.whatsapp_url) {
        const win = window.open(res.data.whatsapp_url, '_blank');
        if (!win || win.closed || typeof win.closed === 'undefined') {
          setPendingWaUrl(res.data.whatsapp_url);
          toast('WhatsApp n\'a pas pu s\'ouvrir. Cliquez sur le bouton ci-dessous.', { icon: '⚠️' });
        } else {
          if (res.data.whatsapp_sent) {
            toast.success('Invitation créée et message WhatsApp envoyé !');
          } else {
            toast.success('Invitation créée — ouverture WhatsApp...');
          }
        }
      } else {
        toast.success('Invitation créée');
      }

      setPhone('');
      setEmail('');
      setMessage('');
      loadInvitations();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erreur');
    }
    setSending(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette invitation ?')) return;
    try {
      await deleteInvitation(id);
      toast.success('Invitation supprimée');
      loadInvitations();
    } catch (err) {
      toast.error('Erreur');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin');
  };

  const selfInvites = invitations.filter(i => i.invited_by === 'self');
  const adminInvites = invitations.filter(i => i.invited_by === 'admin');
  const usedCount = invitations.filter(i => i.is_used).length;

  return (
    <div style={{ minHeight: '100vh', background: '#f8faf8' }}>
      {/* Top bar */}
      <div style={{ background: 'white', borderBottom: '1px solid #e8e8e8', padding: '16px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button onClick={() => navigate('/admin/dashboard')} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', color: '#666', fontSize: '14px' }}>
            <ChevronLeft size={18} /> Retour
          </button>
          <h1 style={{ fontSize: '18px', fontWeight: '700', color: '#1a1a1a', margin: 0 }}>Invitations</h1>
        </div>
        <button onClick={handleLogout} style={{ background: 'none', border: 'none', color: '#c62828', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px' }}>
          <LogOut size={16} /> Déconnexion
        </button>
      </div>

      <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '24px' }}>
        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px', marginBottom: '24px' }}>
          <StatCard label="Total invitations" value={invitations.length} icon={<Users size={20} color="#2E7D32" />} />
          <StatCard label="Auto-invités" value={selfInvites.length} icon={<MessageSquare size={20} color="#42A5F5" />} />
          <StatCard label="Invités par admin" value={adminInvites.length} icon={<Send size={20} color="#FFA726" />} />
          <StatCard label="Liens utilisés" value={usedCount} icon={<CheckCircle size={20} color="#66BB6A" />} />
        </div>

        {/* Create invitation */}
        <div style={{ background: 'white', borderRadius: '16px', padding: '24px', marginBottom: '24px', border: '1px solid #e8e8e8' }}>
          <h2 style={{ fontSize: '16px', fontWeight: '700', color: '#1a1a1a', marginBottom: '16px' }}>Nouvelle invitation</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', marginBottom: '12px' }}>
            <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Numéro WhatsApp" 
              style={{ padding: '12px 14px', borderRadius: '10px', border: '1.5px solid #e0e0e0', fontSize: '14px', outline: 'none' }} />
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email (optionnel)" 
              style={{ padding: '12px 14px', borderRadius: '10px', border: '1.5px solid #e0e0e0', fontSize: '14px', outline: 'none' }} />
          </div>
          <textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Message personnalisé (optionnel)" 
            style={{ width: '100%', padding: '12px 14px', borderRadius: '10px', border: '1.5px solid #e0e0e0', fontSize: '14px', outline: 'none', resize: 'none', minHeight: '60px', marginBottom: '12px', fontFamily: 'Inter, sans-serif' }} />
          <button onClick={handleCreate} disabled={sending}
            style={{ background: sending ? '#e0e0e0' : 'linear-gradient(135deg, #2E7D32, #4CAF50)', color: 'white', border: 'none', padding: '12px 24px', borderRadius: '10px', fontSize: '14px', fontWeight: '600', cursor: sending ? 'default' : 'pointer' }}>
            {sending ? 'Envoi...' : 'Créer et envoyer'}
          </button>
          {pendingWaUrl && (
            <div style={{ marginTop: '12px', padding: '12px', background: '#FFF8E1', borderRadius: '10px', display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ fontSize: '13px', color: '#E65100' }}>Le navigateur a bloqué l'ouverture WhatsApp.</span>
              <a href={pendingWaUrl} target="_blank" rel="noopener noreferrer"
                style={{ background: '#25D366', color: 'white', padding: '8px 16px', borderRadius: '8px', fontSize: '13px', fontWeight: '600', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                <Send size={14} /> Ouvrir WhatsApp
              </a>
            </div>
          )}
        </div>

        {/* Lists */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '24px' }}>
          <InvitationList title="Invités par le Maire" invitations={adminInvites} onDelete={handleDelete} color="#FFA726" />
          <InvitationList title="Auto-invités (site web)" invitations={selfInvites} onDelete={handleDelete} color="#42A5F5" />
        </div>
      </div>
    </div>
  );
};

function StatCard({ label, value, icon }) {
  return (
    <div style={{ background: 'white', borderRadius: '12px', padding: '16px', border: '1px solid #e8e8e8', display: 'flex', alignItems: 'center', gap: '12px' }}>
      <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: '#f8faf8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {icon}
      </div>
      <div>
        <div style={{ fontSize: '20px', fontWeight: '700', color: '#1a1a1a' }}>{value}</div>
        <div style={{ fontSize: '12px', color: '#888' }}>{label}</div>
      </div>
    </div>
  );
}

function InvitationList({ title, invitations, onDelete, color }) {
  return (
    <div style={{ background: 'white', borderRadius: '16px', padding: '20px', border: '1px solid #e8e8e8' }}>
      <h3 style={{ fontSize: '15px', fontWeight: '700', color, marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
        <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: color }} />
        {title} ({invitations.length})
      </h3>
      {invitations.length === 0 ? (
        <p style={{ color: '#888', fontSize: '13px', textAlign: 'center', padding: '20px' }}>Aucune invitation</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {invitations.map(inv => (
            <div key={inv.id} style={{ padding: '12px', borderRadius: '10px', background: inv.is_used ? '#f0f8f0' : '#fafafa', border: `1px solid ${inv.is_used ? '#c8e6c9' : '#eee'}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontSize: '13px', fontWeight: '600', color: '#1a1a1a' }}>
                  {inv.phone || inv.email || 'Sans contact'}
                </div>
                <div style={{ fontSize: '11px', color: '#888', marginTop: '2px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  {inv.is_used ? (
                    <><CheckCircle size={10} color="#2E7D32" /> Utilisé le {new Date(inv.used_at).toLocaleDateString('fr-FR')}</>
                  ) : (
                    <><Clock size={10} color="#999" /> En attente</>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '6px' }}>
                <button onClick={() => { navigator.clipboard.writeText(inv.invite_token); toast.success('Token copié'); }} 
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#666', padding: '4px' }} title="Copier le token">
                  <Copy size={14} />
                </button>
                <button onClick={() => onDelete(inv.id)} 
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#c62828', padding: '4px' }} title="Supprimer">
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default AdminInvitations;
